import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PortalSiteEntity } from './portal-site.entity';

export type PortalSiteDomainTlsStatus =
	| 'notApplicable'
	| 'pending'
	| 'issued'
	| 'failed';

/**
 * Whether a background provisioning/teardown job is currently in flight for this
 * domain. Drives the admin UI spinner and gates concurrent actions:
 *   - 'provisioning' — a PROVISION job is creating the CloudFront tenant /
 *     polling cert issuance / applying the cert.
 *   - 'removing'     — a DEPROVISION job is tearing the domain down. An
 *     in-flight PROVISION job sees this and aborts (delete wins).
 *   - null           — settled; no job running (the steady state, where
 *     `tlsStatus` is authoritative).
 */
export type PortalSiteDomainProvisioningState = 'provisioning' | 'removing';

// One row per hostname a portal_site answers on. isCustom = 0 is the derived
// workd URL (<alias>-<tenant>.my.workd.com) and is served by the shared
// wildcard CloudFront tenant; isCustom = 1 hostnames are customer vanity
// domains, each provisioned its own CloudFront distribution tenant + cert.
//
// This table records only what the TENANT needs to know about a host: its
// TLS/CloudFront provisioning state. The hostname -> instance routing map
// lives in the DVP `_origin` table (login DB), keyed by hostname — crm-api
// registers/removes those rows over HTTP (login /origin) on validate/remove.
// There is intentionally no id handshake back to `_origin`: both register
// (on validate -> issued) and remove (by hostname) are idempotent.
@Index('UK_portal_site_domain_hostname', ['hostname'], { unique: true })
@Index('fk_portal_site__id', ['fkPortalSiteId'], {})
@Entity('portal_site_domain')
export class PortalSiteDomainEntity extends BaseEntity {
	@Column('int', { name: 'fk_portal_site__id', unsigned: true })
	public fkPortalSiteId!: number;

	@Column('varchar', { name: 'hostname', length: 150 })
	public hostname!: string;

	@Column('tinyint', {
		name: 'isCustom',
		width: 1,
		default: () => "'0'",
	})
	public isCustom!: boolean;

	@Column('enum', {
		name: 'tlsStatus',
		enum: ['notApplicable', 'pending', 'issued', 'failed'],
		default: 'notApplicable',
	})
	public tlsStatus!: PortalSiteDomainTlsStatus;

	@Column('varchar', { name: 'tlsCertArn', nullable: true, length: 256 })
	public tlsCertArn!: string | null;

	// CloudFront SaaS Manager distribution-tenant id for this custom hostname
	// (null for the derived workd URL, which is served by the shared wildcard
	// tenant). Stored so crm-api can poll status / delete the tenant later.
	@Column('varchar', {
		name: 'cfDistributionTenantId',
		nullable: true,
		length: 64,
	})
	public cfDistributionTenantId!: string | null;

	@Column('tinyint', {
		name: 'isPrimary',
		width: 1,
		default: () => "'0'",
	})
	public isPrimary!: boolean;

	@Column('tinyint', {
		name: 'isActive',
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean;

	// Null when no background job is in flight (steady state). Set to
	// 'provisioning' / 'removing' while a queued job is working the domain, so
	// the admin UI can show a spinner that survives a page refresh and so a
	// delete can pre-empt an in-flight validate (see the type's doc comment).
	@Column('enum', {
		name: 'provisioningState',
		enum: ['provisioning', 'removing'],
		nullable: true,
		default: null,
	})
	public provisioningState!: PortalSiteDomainProvisioningState | null;

	@ManyToOne(
		() => PortalSiteEntity,
		(portalSite) => portalSite.portalSiteDomains,
		{
			onDelete: 'CASCADE',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([{ name: 'fk_portal_site__id', referencedColumnName: 'id' }])
	public fkPortalSite!: PortalSiteEntity;
}
